import { useMemo } from 'react';

import { V1SecretState } from '@/generated/hooks/gringotts/models';
import { useListSecrets } from '@/generated/hooks/gringotts/v1/services/secret_service';
import { useGetSecretType } from '@/generated/hooks/gringotts/v1/services/secret_type_service';

import type { TCredential } from '../types';
import {
  GRINGOTTS_MAX_PAGE_SIZE,
  isCredentialUsableInWorkspace,
  secretTypeFilter,
} from '../utils';

/**
 * Credentials a connection in this workspace can use for one authentication
 * method: same secret type, living in the organization or this workspace,
 * newest first. Reads the organization list (the workspace list omits
 * organization-scoped credentials) filtered server-side by type.
 *
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @param secretTypeName - The auth method's secret type name.
 * @param enabled - Whether to fetch.
 * @returns The compatible credentials, the secret type, and query state.
 */
export function useCompatibleCredentials(
  organizationId: string,
  workspaceId: string,
  secretTypeName: string,
  enabled = true,
) {
  const secretTypeQuery = useGetSecretType(secretTypeName, {
    query: { enabled: enabled && !!secretTypeName },
  });
  const secretType = secretTypeQuery.data?.data;
  const secretTypeId = secretType?.id ?? '';

  const credentialsQuery = useListSecrets(
    organizationId,
    {
      filter: secretTypeFilter(secretTypeId),
      page_size: GRINGOTTS_MAX_PAGE_SIZE,
    },
    { query: { enabled: enabled && !!organizationId && !!secretTypeId } },
  );

  const credentials = useMemo<TCredential[]>(
    () =>
      (credentialsQuery.data?.data.secrets ?? [])
        .filter(
          credential =>
            credential.state !==
              V1SecretState.SECRET_STATE_PENDING_AUTHORIZATION &&
            isCredentialUsableInWorkspace(
              credential,
              organizationId,
              workspaceId,
            ),
        )
        .sort((a, b) =>
          (b.update_time ?? '').localeCompare(a.update_time ?? ''),
        ),
    [credentialsQuery.data, organizationId, workspaceId],
  );

  return {
    credentials,
    secretType,
    isLoading: secretTypeQuery.isLoading || credentialsQuery.isLoading,
    isError: secretTypeQuery.isError || credentialsQuery.isError,
  };
}
